import CreateInvoice from "@/components/dialog/contributor/CreateInvoice";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { DateConverter, formatAmount, STALE_TIME_INVOICE } from "@/constants";
import { ContributorPerson, useAuth } from "@/context/Auth/CombinedAuthContext";
import { cn } from "@/lib/utils";
import { getContributorInvoices } from "@/services/external-contributors-functions";
import { ContributorInvoice } from "@/types/external-contributors.types";
import { BsExclamationCircleFill } from "react-icons/bs";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
	const user = useAuth().person as ContributorPerson;

	const navigate = useNavigate();

	const userInvoices = useQuery({
		queryKey: ["invoice", "user", user.username],
		queryFn: () => getContributorInvoices(user.accessToken),
		staleTime: STALE_TIME_INVOICE,
		refetchOnMount: "always",
	});
	return (
		<>
			<div className="flex flex-col md:flex-row justify-between gap-2 items-center mb-3">
				<h1 className="text-4xl font-bold">My Invoices</h1>
				<CreateInvoice />
			</div>

			<Table className="border">
				<TableHeader className="">
					<TableRow>
						<TableHead>Claim No.</TableHead>
						<TableHead>Amount</TableHead>
						<TableHead className="hidden xl:table-cell">Claim Type</TableHead>
						<TableHead>Status</TableHead>
						<TableHead className="hidden xl:table-cell">Raised On</TableHead>
						<TableHead>Actions</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{userInvoices.isLoading ? (
						Array.from({ length: 6 }).map((_, i) => (
							<TableRow key={i}>
								<TableCell>
									<Skeleton className="w-full h-10" />
								</TableCell>
								<TableCell>
									<Skeleton className="w-full h-10" />
								</TableCell>
								<TableCell>
									<Skeleton className="w-full h-10" />
								</TableCell>
								<TableCell>
									<Skeleton className="w-full h-10" />
								</TableCell>
								<TableCell className="hidden xl:table-cell">
									<Skeleton className="w-full h-10" />
								</TableCell>
								<TableCell>
									<Skeleton className="w-full h-10" />
								</TableCell>
								<TableCell className="hidden xl:table-cell">
									<Skeleton className="w-full h-10" />
								</TableCell>
							</TableRow>
						))
					) : userInvoices.isError ? (
						<div className="align-centre">
							<h5 className="text-center">Error loading claims.</h5>
						</div>
					) : userInvoices.data ? (
						userInvoices.data.map((invoice: ContributorInvoice) => (
							<TableRow
								key={invoice.invoicemasterid}
								className={cn(
									"cursor-pointer",
									(invoice.manageramend || invoice.financeamend) && "bg-orange-50"
								)}
								onClick={() =>
									navigate(`/contributor-invoice/${invoice.invoicemasterid}`)
								}
							>
								<TableCell style={{ verticalAlign: "middle" }}>
									{invoice.manageramend || invoice.financeamend ? (
										<BsExclamationCircleFill className="inline-block mr-2 text-orange-500 h-4 w-4 mb-0.5" />
									) : null}
									<span>{invoice.invoicemasterid}</span>
								</TableCell>
								<TableCell className="font-medium">
									{formatAmount(invoice.amount, invoice.country ? "USD" : "Ksh")}
								</TableCell>
								<TableCell
									style={{ verticalAlign: "middle" }}
									className="hidden xl:table-cell"
								>
									{invoice.invoicetype}
								</TableCell>
								<TableCell style={{ verticalAlign: "middle" }} className="text-left">
									{invoice.collected ? (
										<span className="text-left text-green-600 bg-green-100 rounded text-xs px-2 py-1">
											Cash Collected
										</span>
									) : invoice.secondaryfinancetobeapprovedby ? (
										<span className="text-left text-violet-600 bg-violet-100 rounded text-xs px-2 py-1">
											Awaiting Finance
										</span>
									) : invoice.financeapproved ? (
										<span className="text-left text-orange-600 bg-orange-100 rounded text-xs px-2 py-1">
											Awaiting Collection
										</span>
									) : invoice.financerejected ? (
										<span className="text-left text-red-600 bg-red-100 rounded text-xs px-2 py-1">
											Finance Disapproved
										</span>
									) : invoice.financeamend ? (
										<span className="text-left text-orange-600 bg-orange-100 rounded text-xs px-2 py-1">
											Changes Requested
										</span>
									) : invoice.secondarymanagertobeapprovedby ? (
										<span className="text-left text-violet-600 bg-violet-100 rounded text-xs px-2 py-1">
											Awaiting Manager
										</span>
									) : invoice.managerapproved ? (
										<span className="text-left text-purple-600 bg-purple-100 rounded text-xs px-2 py-1">
											Awaiting Finance
										</span>
									) : invoice.managerrejected ? (
										<span className="text-left text-red-600 bg-red-100 rounded text-xs px-2 py-1">
											Manager Disapproved
										</span>
									) : invoice.manageramend ? (
										<span className="text-left text-orange-600 bg-orange-100 rounded text-xs px-2 py-1">
											Changes Requested
										</span>
									) : (
										<span className="text-left text-violet-600 bg-violet-100 rounded text-xs px-2 py-1">
											Awaiting Manager
										</span>
									)}
								</TableCell>
								<TableCell className="hidden xl:table-cell">
									{DateConverter(invoice.raisedon)}
								</TableCell>
								<TableCell>
									{invoice.manageramend || invoice.financeamend ? (
										<Button variant="link" className="h-auto p-0">
											Edit
										</Button>
									) : null}
								</TableCell>
							</TableRow>
						))
					) : null}
				</TableBody>
			</Table>
		</>
	);
};

export default Dashboard;
